"use client";

import { useState, useEffect, useRef } from "react";

interface AuthState {
  hasSession: boolean;
  accountHref: string;
  accountLabel: string;
}

function resolveAuthState(): AuthState {
  if (typeof window === "undefined") {
    return {
      hasSession: false,
      accountHref: "/dashboard",
      accountLabel: "Oturum açık",
    };
  }

  const rawUser = localStorage.getItem("auth_user");
  const recentLogoutAt = Number(
    localStorage.getItem("auth_recent_logout_at") ?? 0
  );
  const isRecentLogout =
    Number.isFinite(recentLogoutAt) &&
    recentLogoutAt > 0 &&
    Date.now() - recentLogoutAt < 90_000;

  if (!rawUser || isRecentLogout) {
    return {
      hasSession: false,
      accountHref: "/dashboard",
      accountLabel: "Oturum açık",
    };
  }

  let role: string | undefined;
  let firstName = "";
  try {
    const parsed = JSON.parse(rawUser);
    role = parsed?.role;
    firstName = String(parsed?.firstName || "").trim();
  } catch {
    // ignore parse errors
  }

  return {
    hasSession: true,
    accountHref: role === "SUPER_ADMIN" ? "/super-admin" : "/dashboard",
    accountLabel: firstName ? `${firstName} ile oturum açık` : "Oturum açık",
  };
}

export function useAuthState() {
  const [state, setState] = useState<AuthState>({
    hasSession: false,
    accountHref: "/dashboard",
    accountLabel: "Oturum açık",
  });
  const [mounted, setMounted] = useState(false);
  const initRef = useRef(false);

  useEffect(() => {
    setMounted(true);
    if (initRef.current) return;
    initRef.current = true;
    setState(resolveAuthState());
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onStorage = (event: StorageEvent) => {
      if (event.key !== "auth_sync_event" || !event.newValue) return;
      try {
        const payload = JSON.parse(event.newValue) as { type?: string };
        if (payload?.type === "logout") {
          setState({
            hasSession: false,
            accountHref: "/dashboard",
            accountLabel: "Oturum açık",
          });
        }
        if (payload?.type === "login") {
          setState(resolveAuthState());
        }
      } catch {
        // ignore invalid storage payload
      }
    };

    window.addEventListener("storage", onStorage);
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel("auth_session");
      channel.onmessage = (event) => {
        const type = event.data?.type;
        if (type === "logout") {
          setState({
            hasSession: false,
            accountHref: "/dashboard",
            accountLabel: "Oturum açık",
          });
        }
        if (type === "login") {
          setState(resolveAuthState());
        }
      };
    } catch {
      channel = null;
    }

    return () => {
      window.removeEventListener("storage", onStorage);
      if (channel) channel.close();
    };
  }, []);

  return { ...state, mounted };
}
